/*
  A pasted or dropped picture, made fit to send.

  Screenshots are the common case, and a 4K one is eight megapixels of mostly flat colour - far more
  than anyone reads in an email, and base64'd into a Graph request, most of the 4 MB it accepts. So:

    * the longest side comes down to 1600px, still sharp at any width an email is read at;
    * it is encoded both ways - PNG, and JPEG at 0.9 - and the smaller kept. A picture with
      transparency stays PNG, since JPEG would paint its clear parts black;
    * whatever is still over 1 MB steps down, JPEG quality first and then size, until it fits.

  The 1 MB is the server's cap on one picture (InlineImages.MaxImageBytes); the per-message budgets
  sit on top of it.
*/

const MAX_SIDE = 1600;
export const MAX_IMAGE_BYTES = 1024 * 1024;

const JPEG_QUALITY = 0.9;
const MIN_JPEG_QUALITY = 0.5;

// Each size step keeps this much of the width and height.
const SHRINK = 0.8;

export interface INormalizedImage {
  // A data: URI - what the editor holds until the message is sent.
  src: string;
  width: number;
  height: number;
  bytes: number;
}

async function loadImage(url: string): Promise<HTMLImageElement> {
  const img = new Image();
  img.src = url;
  await img.decode();
  return img;
}

function drawAt(img: HTMLImageElement, width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser cannot resize images');

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(img, 0, 0, width, height);

  return canvas;
}

function hasTransparency(canvas: HTMLCanvasElement): boolean {
  const context = canvas.getContext('2d');
  if (!context) return false;

  const { data } = context.getImageData(0, 0, canvas.width, canvas.height);

  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) return true;
  }

  return false;
}

const encode = (canvas: HTMLCanvasElement, type: string, quality?: number) =>
  new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('The image could not be encoded'))),
      type,
      quality
    )
  );

const smaller = (a: Blob, b: Blob) => (b.size < a.size ? b : a);

const toDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

export default async function normalizeImage(source: Blob): Promise<INormalizedImage> {
  const url = URL.createObjectURL(source);

  try {
    const img = await loadImage(url);

    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;
    if (!naturalWidth || !naturalHeight) throw new Error('The image has no size');

    const scale = Math.min(1, MAX_SIDE / Math.max(naturalWidth, naturalHeight));
    let width = Math.max(1, Math.round(naturalWidth * scale));
    let height = Math.max(1, Math.round(naturalHeight * scale));

    let canvas = drawAt(img, width, height);
    const transparent = source.type !== 'image/jpeg' && hasTransparency(canvas);

    let best = await encode(canvas, 'image/png');
    if (!transparent) best = smaller(best, await encode(canvas, 'image/jpeg', JPEG_QUALITY));

    // Already a PNG or JPEG that needed no resizing: re-encoding it can only lose, never gain.
    if (scale === 1 && (source.type === 'image/png' || source.type === 'image/jpeg')) {
      best = smaller(best, source);
    }

    let quality = JPEG_QUALITY;

    while (best.size > MAX_IMAGE_BYTES) {
      if (!transparent && quality > MIN_JPEG_QUALITY) {
        quality = Math.round((quality - 0.1) * 10) / 10;
        // eslint-disable-next-line no-await-in-loop
        best = await encode(canvas, 'image/jpeg', quality);
      } else {
        width = Math.round(width * SHRINK);
        height = Math.round(height * SHRINK);
        if (width < 1 || height < 1) throw new Error('The image cannot be made small enough');

        canvas = drawAt(img, width, height);
        // eslint-disable-next-line no-await-in-loop
        best = transparent
          ? await encode(canvas, 'image/png')
          : await encode(canvas, 'image/jpeg', quality);
      }
    }

    return { src: await toDataUrl(best), width, height, bytes: best.size };
  } finally {
    URL.revokeObjectURL(url);
  }
}
